import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { 
  BarChart3, Download, Calendar as CalendarIcon, TrendingUp, 
  ShoppingCart, DollarSign, Clock, Printer
} from "lucide-react";
import { format } from "date-fns";
import { formatCurrency, cn } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerReports = () => {
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 7)),
    to: new Date(),
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/orders`, {
        params: {
          date_from: dateRange.from.toISOString(),
          date_to: dateRange.to.toISOString(),
        },
      });
      setReport(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (exportFormat) => {
    try {
      const response = await axios.get(`${API}/reports/export`, {
        params: {
          date_from: dateRange.from.toISOString(),
          date_to: dateRange.to.toISOString(),
          format_type: exportFormat,
        },
      });

      const fromStr = format(dateRange.from, "yyyy-MM-dd");
      const toStr = format(dateRange.to, "yyyy-MM-dd");

      if (exportFormat === "csv") {
        const blob = new Blob([response.data.csv_data || ""], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dalabsom-orders-${fromStr}-to-${toStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("CSV downloaded!");
      } else if (exportFormat === "json") {
        const blob = new Blob([JSON.stringify(response.data.orders || [], null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `dalabsom-orders-${fromStr}-to-${toStr}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("JSON downloaded!");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  const handlePrintPDF = () => {
    if (!report) { toast.error("No report data"); return; }
    const fromStr = format(dateRange.from, "MMM d, yyyy");
    const toStr = format(dateRange.to, "MMM d, yyyy");
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Popup blocked. Allow popups to print."); return; }
    const rows = (report.orders || []).map(o =>
      `<tr><td>${o.order_number}</td><td>${format(new Date(o.created_at), "MMM d, HH:mm")}</td><td>${o.order_type}</td><td>${formatCurrency(o.total)}</td><td>${o.payment_status}</td><td>${o.payment_method || ""}</td><td>${o.status}</td></tr>`
    ).join("");
    w.document.write(`<!doctype html><html><head><title>Dalabsom Report ${fromStr} - ${toStr}</title><style>
      body{font-family:system-ui,sans-serif;padding:24px;color:#111}
      h1{font-size:20px;margin:0 0 4px}
      .meta{color:#666;margin-bottom:24px;font-size:13px}
      .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
      .kpi{border:1px solid #e5e7eb;border-radius:8px;padding:12px}
      .kpi .l{font-size:11px;color:#666;text-transform:uppercase}
      .kpi .v{font-size:20px;font-weight:700;margin-top:4px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #eee}
      th{background:#f9fafb;font-weight:600}
      @media print{button{display:none}}
    </style></head><body>
      <h1>Dalabsom — Orders Report</h1>
      <div class="meta">${fromStr} → ${toStr}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Total Orders</div><div class="v">${report.total_orders || 0}</div></div>
        <div class="kpi"><div class="l">Revenue</div><div class="v">${formatCurrency(report.total_revenue || 0)}</div></div>
        <div class="kpi"><div class="l">Paid</div><div class="v">${report.paid_orders || 0}</div></div>
        <div class="kpi"><div class="l">Unpaid</div><div class="v">${report.unpaid_orders || 0}</div></div>
      </div>
      <table><thead><tr><th>Order #</th><th>Date</th><th>Type</th><th>Total</th><th>Payment</th><th>Method</th><th>Status</th></tr></thead><tbody>${rows || '<tr><td colspan="7" style="text-align:center;color:#999;padding:24px">No orders in range</td></tr>'}</tbody></table>
      <script>setTimeout(()=>{window.print();},300);</script>
    </body></html>`);
    w.document.close();
  };

  const stats = [
    {
      title: "Total Orders",
      value: report?.total_orders || 0,
      icon: ShoppingCart,
      color: "bg-blue-500",
      bgColor: "bg-blue-50",
    },
    {
      title: "Total Revenue",
      value: formatCurrency(report?.total_revenue || 0),
      icon: DollarSign,
      color: "bg-green-500",
      bgColor: "bg-green-50",
    },
    {
      title: "Paid Orders",
      value: report?.paid_orders || 0,
      icon: TrendingUp,
      color: "bg-emerald-500",
      bgColor: "bg-emerald-50",
    },
    {
      title: "Unpaid Orders",
      value: report?.unpaid_orders || 0,
      icon: Clock,
      color: "bg-amber-500",
      bgColor: "bg-amber-50",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="manager-reports">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Reports</h1>
              <p className="text-sm text-muted-foreground">View sales and order analytics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal" data-testid="date-range-picker">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="grid grid-cols-3 gap-1 p-3 border-b text-xs">
                  {[
                    { label: "Today", days: 0, from: new Date(new Date().setHours(0,0,0,0)) },
                    { label: "Yesterday", days: 1 },
                    { label: "Last 7", days: 7 },
                    { label: "Last 30", days: 30 },
                    { label: "This month", month: true },
                    { label: "All time", all: true },
                  ].map(p => (
                    <button key={p.label}
                      className="px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-700 font-medium"
                      onClick={() => {
                        const now = new Date();
                        if (p.label === "Today") { setDateRange({ from: new Date(new Date().setHours(0,0,0,0)), to: now }); return; }
                        if (p.label === "Yesterday") {
                          const y = new Date(); y.setDate(y.getDate() - 1);
                          const start = new Date(y); start.setHours(0,0,0,0);
                          const end = new Date(y); end.setHours(23,59,59,999);
                          setDateRange({ from: start, to: end }); return;
                        }
                        if (p.label === "Last 7") {
                          const f = new Date(); f.setDate(f.getDate() - 7); f.setHours(0,0,0,0);
                          setDateRange({ from: f, to: now }); return;
                        }
                        if (p.label === "Last 30") {
                          const f = new Date(); f.setDate(f.getDate() - 30); f.setHours(0,0,0,0);
                          setDateRange({ from: f, to: now }); return;
                        }
                        if (p.label === "This month") {
                          const f = new Date(now.getFullYear(), now.getMonth(), 1);
                          setDateRange({ from: f, to: now }); return;
                        }
                        if (p.label === "All time") {
                          setDateRange({ from: new Date(2020, 0, 1), to: now });
                        }
                      }}
                      data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.from, to: dateRange.to }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      setDateRange({ from: range.from, to: range.to });
                    }
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            <Button variant="outline" onClick={() => handleExport("csv")} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={() => handleExport("json")} data-testid="export-json-btn">
              <Download className="w-4 h-4 mr-2" />
              JSON
            </Button>
            <Button variant="outline" onClick={handlePrintPDF} data-testid="export-pdf-btn">
              <Printer className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                      <stat.icon className={`w-6 h-6 ${stat.color.replace("bg-", "text-")}`} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Recent Orders Table */}
        {report?.orders && report.orders.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Order #</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Date</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Total</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Payment</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.orders.slice(0, 20).map((order) => (
                      <tr key={order.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="py-3 px-2 font-mono text-sm">{order.order_number}</td>
                        <td className="py-3 px-2 text-sm">{format(new Date(order.created_at), "MMM d, HH:mm")}</td>
                        <td className="py-3 px-2 text-sm capitalize">{order.order_type.replace("_", " ")}</td>
                        <td className="py-3 px-2 text-sm font-medium">{formatCurrency(order.total)}</td>
                        <td className="py-3 px-2">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            order.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          )}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            order.status === "completed" ? "bg-gray-100 text-gray-700" :
                            order.status === "ready" ? "bg-green-100 text-green-700" :
                            order.status === "preparing" ? "bg-amber-100 text-amber-700" :
                            "bg-blue-100 text-blue-700"
                          )}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManagerReports;
